import { HStack, Box, IconButton, Text } from '@cad-arc';
import { QueryableEntityType } from 'mark43-federated-search';
import { FormInputIconButton } from '../../../../forms/integrated-form-components/form-button';
import React, { useCallback, useMemo } from 'react';
import {
    EventWorkspacePanelDexFormPrefill,
    EventWorkspacePanelDexNextFormPrefill,
} from '../../../workspace-panel-types';
import { UseFormReturn, useWatch } from 'react-hook-form';
import { UseTrackedFieldArrayRemove } from '../../../../forms/use-tracked-field-array';
import { format } from 'date-fns';
import { useApplicationType } from '../../../../common/hooks/use-application-type';
import { EntityPanelFormPersonBaseKey, EntityPanelPersonsForm } from '../types';
import { useOpenDepartmentLink } from '../../../../layout/use-open-department-link';
import { useDrawerControls } from '../../../../drawers/components/drawer-context-provider';
import {
    useEventNotifications,
    useRemoveEventNotifications,
} from '../../../../event-notifications/use-event-notifications';
import { NotificationCounter } from '../../../../common/components/notification-counter';
import { observer } from 'mobx-react-lite';
import { useEnabledFeatures } from '../../../../common/hooks/enabled-features/use-enabled-features';
import { getUniversalSearchFormLabel } from '../../../../common/helpers/get-usx-labels';
import { useHandleOpenInDexButtonClick } from '../../../hooks/use-handle-open-in-dex-button-click';
import { useIsDexFullyOperational } from '../../../../federated-search/federated-search-remote-state-context';

export function MasterPersonLinkButton({
    isPersisted,
    masterPersonId,
}: {
    isPersisted?: boolean;
    masterPersonId: number | null | undefined;
}) {
    const openDepartmentLink = useOpenDepartmentLink();
    const enabledFeatures = useEnabledFeatures();
    if (!enabledFeatures.masterProfilesEnabled || !isPersisted || !masterPersonId) {
        return null;
    }
    return (
        <IconButton
            size="sm"
            style={{ alignSelf: 'flex-start' }}
            icon="OpenNewWindow"
            variant="ghost"
            aria-label="Open Person Profile in RMS"
            onClick={() => {
                openDepartmentLink({
                    path: `/profiles/persons/${masterPersonId}/details`,
                    app: 'RMS',
                    tenantStrategy: 'PREFER_RMS_TENANT',
                });
            }}
            tabIndex={-1}
        />
    );
}

export const EntityPanelFormPersonCardControls = observer(
    function EntityPanelFormPersonCardControls({
        remove,
        allowMembersControl,
        isNewEntity,
        form,
        baseKey,
        index,
        isReadOnly,
        entityNumber,
        eventId,
    }: {
        entityNumber?: string;
        eventId?: number;
        isNewEntity: boolean;
        allowMembersControl?: boolean;
        form: UseFormReturn<EntityPanelPersonsForm>;
        remove: UseTrackedFieldArrayRemove;
        baseKey: EntityPanelFormPersonBaseKey;
        index: number;
        isReadOnly?: boolean;
    }) {
        const isReportingParty =
            baseKey === 'reportingParties' || baseKey === 'mergedReportingParties';
        const { isFirstResponder } = useApplicationType();
        const drawerControls = useDrawerControls();
        const enabledFeatures = useEnabledFeatures();
        const isDexFullyOperational = useIsDexFullyOperational();

        const handleOpenInDexButtonClick = useHandleOpenInDexButtonClick({
            dexFormQuery: 'PERSON_MASTER_QUERY',
            dexNextEntityType: QueryableEntityType.PERSON,
            getDexFormValues,
            getDexNextFormValues,
        });

        const name = useWatch({
            control: form.control,
            name: [`${baseKey}.${index}.firstName`, `${baseKey}.${index}.lastName`],
        });

        const personId = useWatch({
            control: form.control,
            name: `${baseKey}.${index}.contextId`,
        });

        const personProfileId = useWatch({
            control: form.control,
            name: `${baseKey}.${index}.personProfileId`,
        });

        const isPersisted = useWatch({
            control: form.control,
            name: `${baseKey}.${index}.isPersisted`,
        });

        const eventMasterPersonData = useWatch({
            control: form.control,
            name: `${baseKey}.${index}.eventMasterPersonData`,
        });

        const showDexButton =
            !isNewEntity &&
            isPersisted &&
            !isFirstResponder &&
            !isReadOnly &&
            isDexFullyOperational;
        const showDeleteButton = useMemo(() => {
            if (isReadOnly) {
                return false;
            }

            if (isFirstResponder) {
                return baseKey !== 'reportingParties' || index > 0;
            }

            return allowMembersControl;
        }, [isReadOnly, isFirstResponder, allowMembersControl, baseKey, index]);

        const getTitle = useCallback(() => {
            if (isFirstResponder) {
                switch (baseKey) {
                    case 'reportingParties':
                        return `Reporting Party ${index + 1}`;
                    case 'involvedPersons':
                        return `Involved Person ${index + 1}`;
                    case 'mergedReportingParties':
                        return `Merged Reporting Party ${index + 1}`;
                    case 'mergedInvolvedPersons':
                        return `Merged Involved Person ${index + 1}`;
                    default:
                        break;
                }
            }

            const names = name.filter((n) => !!n).join(' ');
            return names == '' ? `Person ${index + 1}` : names;
        }, [isFirstResponder, name, baseKey, index]);

        const getFormValues = () =>
            form.getValues([
                `${baseKey}.${index}.lastName`,
                `${baseKey}.${index}.firstName`,
                `${baseKey}.${index}.middleName`,
                `${baseKey}.${index}.dateOfBirth`,
                `${baseKey}.${index}.height`,
                `${baseKey}.${index}.driversLicenseNumber`,
                `${baseKey}.${index}.driversLicenseState`,
                `${baseKey}.${index}.race`,
                `${baseKey}.${index}.sex`,
                `${baseKey}.${index}.phoneNumber`,
                `${baseKey}.${index}.weight`,
                `${baseKey}.${index}.eyeColor`,
                `${baseKey}.${index}.hairColor`,
                `${baseKey}.${index}.notes`,
            ]);

        function getDexFormValues(): EventWorkspacePanelDexFormPrefill {
            const [
                lastName,
                firstName,
                middleName,
                dateOfBirth,
                height,
                driversLicenseNumber,
                driversLicenseState,
                race,
                sex,
            ] = getFormValues();
            return {
                eventNumber: entityNumber,
                person: {
                    nameLast: lastName ?? undefined,
                    nameFirst: firstName ?? undefined,
                    nameMiddle: middleName ?? undefined,
                    birthDate: dateOfBirth
                        ? // Split on - to fix timezone issues with date.
                          format(new Date(dateOfBirth.split('-').join(',')), 'MMddyyyy')
                        : undefined,
                    height: height ?? undefined,
                    operatorLicenseNumber: driversLicenseNumber ?? undefined,
                    state:
                        driversLicenseState &&
                        driversLicenseState.label &&
                        !driversLicenseState.isOther
                            ? driversLicenseState.label
                            : undefined,
                    raceCode: race?.ncicCode ?? undefined,
                    sexCode: sex?.ncicCode ?? undefined,
                },
            };
        }

        function getDexNextFormValues(): EventWorkspacePanelDexNextFormPrefill {
            const [
                lastName,
                firstName,
                middleName,
                dateOfBirth,
                height,
                driversLicenseNumber,
                driversLicenseState,
                race,
                sex,
                phoneNumber,
                weight,
                eyeColor,
                hairColor,
                notes,
            ] = getFormValues();

            return {
                eventNumber: entityNumber,
                entityType: QueryableEntityType.PERSON,
                prefillValues: {
                    NameFirst: firstName ?? undefined,
                    NameLast: lastName ?? undefined,
                    NameMiddle: middleName ?? undefined,
                    BirthDate: dateOfBirth ?? undefined,
                    Height: height ?? undefined,
                    OperatorLicenseNumber: driversLicenseNumber ?? undefined,
                    NamePhoneNumbers: phoneNumber ? [{ phoneNumber }] : undefined,
                    CAD_EVENT_SELECT_VALUE: eventId ? String(eventId) : undefined,
                    RegistrationState: driversLicenseState?.value
                        ? String(driversLicenseState?.value)
                        : undefined,
                    RaceCode: race?.value ? String(race?.value) : undefined,
                    SexCode: sex?.value ? String(sex?.value) : undefined,
                    Weight: weight ? String(weight) : undefined,
                    EyeColorCode: eyeColor ? String(eyeColor) : undefined,
                    HairColorCode: hairColor ? String(hairColor) : undefined,
                    FreeText: notes ?? undefined,
                },
            };
        }

        function onDelete() {
            form.setValue(`${baseKey}.${index}.isRemoved`, true);
            remove(personId, index);
        }

        const masterPersonMatchNotifications = useEventNotifications({
            entityId: personId ?? undefined,
            notifications: [
                'REPORTING_PARTY_MASTER_SEARCH_RESULTS',
                'INVOLVED_PERSON_MASTER_SEARCH_RESULTS',
            ],
        });

        const removeEventNotifications = useRemoveEventNotifications();

        return (
            <HStack
                padding={2}
                pl={isNewEntity ? 0 : 1}
                spacing={2}
                width="100%"
                alignItems="center"
                data-testid="person-form-section"
                justifyContent="space-between"
            >
                <Text variant="headingSm">{getTitle()}</Text>
                <HStack>
                    {enabledFeatures.masterProfilesEnabled &&
                    isPersisted &&
                    !eventMasterPersonData?.id ? (
                        <Box position="relative">
                            {masterPersonMatchNotifications.length > 0 ? (
                                <NotificationCounter
                                    style={{ position: 'absolute', bottom: '-6px', right: '-6px' }}
                                >
                                    {1}
                                </NotificationCounter>
                            ) : undefined}
                            <IconButton
                                size="sm"
                                style={{ alignSelf: 'flex-start' }}
                                icon="Link"
                                variant="ghost"
                                aria-label="Link to Matched Person Profile"
                                onClick={() => {
                                    removeEventNotifications({
                                        entityId: personId ?? undefined,
                                        notifications: [
                                            'REPORTING_PARTY_MASTER_SEARCH_RESULTS',
                                            'INVOLVED_PERSON_MASTER_SEARCH_RESULTS',
                                        ],
                                    });
                                    if (personProfileId) {
                                        drawerControls.open({
                                            type: 'master-profile-matches',
                                            props: {
                                                entityId: personProfileId,
                                                entityType: 'PERSON_PROFILE',
                                            },
                                        });
                                    }
                                }}
                                tabIndex={-1}
                            />
                        </Box>
                    ) : undefined}{' '}
                    <MasterPersonLinkButton
                        isPersisted={isPersisted}
                        masterPersonId={eventMasterPersonData?.id}
                    />
                    {showDexButton && (
                        <IconButton
                            size="sm"
                            style={{ alignSelf: 'flex-start' }}
                            icon="DataExchangeInquiries"
                            variant="ghost"
                            aria-label={`Open Person ${getUniversalSearchFormLabel(
                                enabledFeatures.dexNextEnabled
                            )}`}
                            onClick={handleOpenInDexButtonClick}
                            data-testid="OPEN_PERSON_IN_DEX"
                            tabIndex={-1}
                        />
                    )}
                    {showDeleteButton ? (
                        <FormInputIconButton
                            style={{ alignSelf: 'flex-start' }}
                            size="sm"
                            aria-label="Delete"
                            variant="ghost"
                            icon="TrashCan"
                            onClick={onDelete}
                            data-testid={
                                isReportingParty
                                    ? `reporting-party-remove-${personId ?? 'new'}`
                                    : `involved-person-remove-${personId ?? 'new'}`
                            }
                            tabIndex={-1}
                        />
                    ) : undefined}
                </HStack>
            </HStack>
        );
    }
);
